import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import type { PaginationFindInterface } from '@zaoblako/shared/common/dtos/pagination-find.dto';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { DropdownEntityCacheService } from '@zaoblako/web/shared/services/dropdown-entity-cache.service';

@Component({
  standalone: true,
  imports: [CommonModule],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export abstract class BaseSingleSelectComponent<T extends { id: number }> implements OnInit, OnChanges, OnDestroy {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public entities: T[] = [];
  public queryParams: DropdownBaseQueryInterface = {
    limit: 20,
    offset: 0
  };

  @Input() public loading = false;
  public paginationLoading = false;
  public count = 0;
  /**
   * True while a selected id from the parent is being resolved via `findById`.
   * Bind to `crm-single-select` `[loading]`.
   */
  public selectionLoading = false;

  private isNowQuery = false;
  private searchSub?: Subscription;
  private registryLoadSub?: Subscription;
  private hydrationSub?: Subscription;

  @Output() public selectedEntityChange = new EventEmitter<T>();

  @Input() public customSearch = false;
  public abstract availableModule: string;
  /**
   * Optional cache bucket for {@link DropdownEntityCacheService}.
   * When set, first page and selected-id hydrate survive remounts (overflow / «Доп фильтры»).
   */
  protected readonly entityCacheKind: string | null = null;

  @Input() public isNotAutoSelect = false;
  @Input() public includeIds: number[] = [];
  @Input() public excludeIds: number[] = [];

  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = false;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = false;
  @Input() public labelInput = false;
  @Input() public placeholder = '\u00A0';
  @Input() public appendTo: string;

  public readonly cdRef = inject(ChangeDetectorRef);
  public readonly globalService = inject(GlobalService);
  protected readonly entityCache = inject(DropdownEntityCacheService);

  public abstract findById(id: number): Observable<T>;
  public abstract findWithPagination(
    queryParams: Partial<DropdownBaseQueryInterface>
  ): Observable<PaginationFindInterface<T>>;

  public _selectedEntity: T;
  @Input() public set selectedEntity(selectedEntity: T) {
    if (this.customSearch || selectedEntity === null) {
      this._selectedEntity = selectedEntity;
      this.selectionLoading = false;
      return;
    }

    if (this.entities.some((entity) => entity.id === selectedEntity?.id)) {
      this._selectedEntity = this.entities.find((entity) => entity.id === selectedEntity.id);
      this.selectionLoading = false;
      return;
    }

    if (selectedEntity?.id) {
      if (typeof selectedEntity.id === 'string') {
        selectedEntity.id = +selectedEntity.id;
      }

      this.hydrateSelectedEntityById(selectedEntity.id);
    }
  }

  @Input() public set setSelectedEntityId(id: number) {
    if (id === null || id === undefined) {
      this.selectionLoading = false;
      return;
    }

    let _id = id;
    if (typeof id === 'string') {
      _id = +id;
    }

    if (this.entities.some((entity) => entity.id === _id)) {
      this._selectedEntity = this.entities.find((entity) => entity.id === _id);
      this.selectionLoading = false;
      return;
    }

    this.hydrateSelectedEntityById(_id);
  }

  public ngOnInit(): void {
    this.firstLoad();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const excludeIdsChanged = changes.excludeIds && !changes.excludeIds.firstChange;
    const includeIdsChanged = changes.includeIds && !changes.includeIds.firstChange;
    if (excludeIdsChanged || includeIdsChanged) {
      this.firstLoad();
    }
  }

  public ngOnDestroy(): void {
    this.unbindScrollPagination();
    this.searchSub?.unsubscribe();
    this.registryLoadSub?.unsubscribe();
    this.hydrationSub?.unsubscribe();
  }

  public onSelect(entity: T) {
    if (this.entityCacheKind && entity) {
      this.entityCache.putEntities(this.entityCacheKind, [entity]);
    }
    this.selectedEntityChange.emit(entity);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.bindScrollPagination();
        if (!this.scrollElement) {
          setTimeout(() => this.bindScrollPagination(), 100);
        }
      }, 50);
    }
  }

  private scrollElement: HTMLElement | null = null;

  private readonly onScrollPagination = (): void => {
    const el = this.scrollElement;
    if (!el) {
      return;
    }

    if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight - 1) {
      this.loadMoreEntities();
    }
  };

  private bindScrollPagination(): void {
    const el = this.getPanelScrollElement();
    if (!el || this.scrollElement === el) {
      return;
    }

    this.unbindScrollPagination();
    this.scrollElement = el;
    el.addEventListener('scroll', this.onScrollPagination, { passive: true });
    this.onScrollPagination();
  }

  private unbindScrollPagination(): void {
    if (this.scrollElement) {
      this.scrollElement.removeEventListener('scroll', this.onScrollPagination);
      this.scrollElement = null;
    }
  }

  private getPanelScrollElement(): HTMLElement | null {
    const overlay = this.singleSelectComponent?.dropdown?.overlayViewChild?.overlayViewChild
      ?.nativeElement as HTMLElement | undefined;
    if (!overlay) {
      return null;
    }

    const virtualScrollElement = overlay.querySelector('.p-scroller') as HTMLElement | null;
    if (virtualScrollElement) {
      return virtualScrollElement;
    }

    return overlay.querySelector('.p-dropdown-items-wrapper') as HTMLElement | null;
  }

  private loadMoreEntities(): void {
    if (
      this.customSearch ||
      this.entities.length >= this.count ||
      this.loading ||
      this.paginationLoading
    ) {
      return;
    }

    this.paginationLoading = true;
    this.cdRef.markForCheck();

    this.findWithPagination({
      ...this.queryParams,
      includeIds: this.includeIds,
      excludeIds: this.excludeIds
    }).subscribe({
      next: (data) => {
        this.entities = this.entities.concat(
          data.items.filter((item) => !this.entities.some((entity) => entity.id === item.id))
        );
        this.count = data.count;
        this.queryParams.offset = this.entities.length;
        this.paginationLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.paginationLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public search(name: string) {
    if (!this.customSearch) {
      if (!name?.length) {
        this.searchSub?.unsubscribe();
        this.queryParams.search = undefined;
        this.queryParams.offset = 0;
        this.loading = true;
        this.cdRef.markForCheck();

        this.searchSub = this.findWithPagination({
          ...this.queryParams,
          includeIds: this.includeIds,
          excludeIds: this.excludeIds
        }).subscribe({
          next: (data) => {
            if (!data.items.some((item) => item.id === this._selectedEntity?.id) && this._selectedEntity) {
              this.entities = data.items.concat(this._selectedEntity);
            } else {
              this.entities = data.items;
            }
            this.count = data.count;
            this.queryParams.offset = this.entities.length;
            this.loading = false;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.loading = false;
            this.cdRef.markForCheck();
          }
        });
        return;
      }

      this.searchSub?.unsubscribe();
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.searchSub = this.findWithPagination({
        ...this.queryParams,
        includeIds: this.includeIds,
        excludeIds: this.excludeIds
      }).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedEntity?.id) && this._selectedEntity) {
            this.entities = data.items.concat(this._selectedEntity);
          } else {
            this.entities = data.items;
          }
          this.count = data.count;
          this.queryParams.offset = this.entities.length;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.singleSelectComponent?.dropdown?.filterValue);
  }

  public onHidePanel(): void {
    this.unbindScrollPagination();
  }

  public firstLoad() {
    if (
      !this.customSearch &&
      this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.[this.availableModule]?.enabled
    ) {
      if (this.loading) {
        this.isNowQuery = true;
        return;
      }
      this.registryLoadSub?.unsubscribe();
      this.loading = true;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      const query = {
        ...this.queryParams,
        includeIds: this.includeIds,
        excludeIds: this.excludeIds
      };

      const load$ = this.entityCacheKind
        ? this.entityCache.loadFirstPage(this.entityCacheKind, this.buildFirstPageFingerprint(), () =>
            this.findWithPagination(query)
          )
        : this.findWithPagination(query);

      this.registryLoadSub = load$.subscribe({
        next: (data) => {
          this.entities = data.items;
          if (this._selectedEntity?.id && !this.entities.some((entity) => entity.id === this._selectedEntity.id)) {
            this.entities = this.entities.concat(this._selectedEntity);
          }
          if (this.entities?.length === 1 && !this.isNotAutoSelect) {
            this._selectedEntity = this.entities[0];
            this.onSelect(this._selectedEntity);
          }
          this.count = data.count;
          // Следующая страница API: уже загружено entities.length записей, не пропуск в UI
          this.queryParams.offset = this.entities.length;
          this.loading = false;
          this.cdRef.markForCheck();

          if (this.isNowQuery) {
            this.isNowQuery = false;
            this.firstLoad();
          }
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();

          if (this.isNowQuery) {
            this.isNowQuery = false;
            this.firstLoad();
          }
        }
      });
    }
  }

  private hydrateSelectedEntityById(id: number): void {
    const applyEntity = (_entity: T, useDelay = true) => {
      if (!this.entities.some((entity) => entity.id === _entity.id)) {
        this.entities = this.entities.concat(_entity);
      }
      if (this.entityCacheKind) {
        this.entityCache.putEntities(this.entityCacheKind, [_entity]);
      }
      if (!useDelay) {
        this._selectedEntity = _entity;
        this.selectionLoading = false;
        this.cdRef.markForCheck();
        return;
      }
      setTimeout(() => {
        this._selectedEntity = _entity;
        this.selectionLoading = false;
        this.cdRef.markForCheck();
      }, 30);
    };

    if (this.entityCacheKind) {
      const { hits, missing } = this.entityCache.getByIds<T>(this.entityCacheKind, [id]);
      if (!missing.length && hits[0]) {
        applyEntity(hits[0], false);
        return;
      }
    }

    this.selectionLoading = true;
    this.cdRef.markForCheck();
    this.hydrationSub?.unsubscribe();

    if (this.entityCacheKind) {
      this.hydrationSub = this.entityCache
        .loadByIds(this.entityCacheKind, [id], (missing) =>
          this.findById(+missing[0]).pipe(map((entity) => [entity]))
        )
        .subscribe({
          next: (entities) => {
            this.hydrationSub = undefined;
            if (entities[0]) {
              applyEntity(entities[0]);
            } else {
              this.clearStaleSelectedEntity();
            }
          },
          error: () => {
            this.hydrationSub = undefined;
            this.selectionLoading = false;
            this.cdRef.markForCheck();
          }
        });
      return;
    }

    this.hydrationSub = this.findById(id).subscribe({
      next: (_entity) => {
        this.hydrationSub = undefined;
        if (_entity) {
          applyEntity(_entity);
        } else {
          this.clearStaleSelectedEntity();
        }
      },
      error: () => {
        this.hydrationSub = undefined;
        this.selectionLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  private clearStaleSelectedEntity(): void {
    this._selectedEntity = undefined;
    this.selectionLoading = false;
    this.selectedEntityChange.emit(undefined);
    this.cdRef.markForCheck();
  }

  private buildFirstPageFingerprint(): string {
    return this.entityCache.fingerprint({
      ...this.queryParams,
      offset: 0,
      includeIds: this.includeIds?.length ? this.includeIds : undefined,
      excludeIds: this.excludeIds?.length ? this.excludeIds : undefined
    });
  }
}
